import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Order, ShippingDetails } from './order-types';
import { useToast } from '@/components/ui/use-toast';
import { useStockManagement } from '@/hooks/useStockManagement';

export const usePurchases = (userId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [confirmingDelivery, setConfirmingDelivery] = useState<string | null>(null);
  const [confirmingReceipt, setConfirmingReceipt] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const { handleIncrementStock } = useStockManagement();
  
  // Fetch purchases data
  const { data: purchases, isLoading, error } = useQuery({
    queryKey: ['userPurchases', userId],
    queryFn: async () => {
      // Get orders where user is the buyer
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id, 
          status, 
          amount, 
          currency, 
          created_at,
          delivery_address,
          shipping_method,
          shipping_details,
          user_id,
          seller_id,
          cancellation_reason,
          requires_confirmation,
          listings(id, title, images, stock)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (ordersError) throw ordersError;
      
      // If we have orders, fetch seller profiles for each order
      if (ordersData && ordersData.length > 0) {
        // Get unique seller IDs
        const sellerIds = [...new Set(ordersData.map(order => order.seller_id))];
        
        // Fetch all relevant seller profiles in a single query
        const { data: sellerProfiles, error: sellerError } = await supabase
          .from('profiles')
          .select('id, full_name, phone_number, email')
          .in('id', sellerIds);
          
        if (sellerError) throw sellerError;
        
        // Attach seller profile to each order
        const ordersWithSellerProfiles = ordersData.map(order => ({
          ...order,
          seller_profile: sellerProfiles?.find(profile => profile.id === order.seller_id) || null
        }));
        
        return ordersWithSellerProfiles as Order[];
      }
      
      return (ordersData || []) as Order[];
    },
  });
  
  // Subscribe to order status changes for real-time updates
  useEffect(() => {
    if (!userId) return;
    
    // Set up realtime subscription for order changes
    const channel = supabase
      .channel('order-status-changes')
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'orders',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        
        // Refresh purchases query to update UI
        queryClient.invalidateQueries({ queryKey: ['userPurchases', userId] });
        
        // Show toast notification
        if (payload.new && payload.old && payload.new.status !== payload.old.status) {
          toast({
            title: "Status porosie përditësuar",
            description: `Porosia juaj është tani në statusin: ${translateStatus(payload.new.status)}`,
            variant: "default",
          });
        }
      })
      .subscribe();
    
    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient, toast]);
  
  // Cancel order mutation with reason
  // NOTE: The stock update call has been removed from here so the stock is only incremented once,
  // at the point of final confirmation.
  const cancelOrderMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string, reason: string }) => {
      // First, optionally get the order details if needed for other purposes
      const { data: orderDetails, error: orderError } = await supabase
        .from('orders')
        .select(`
          id, seller_id, listing_id, 
          listings(id, stock)
        `)
        .eq('id', orderId)
        .single();
      
      if (orderError) throw orderError;
      
      // Now update the order status to cancelled and mark it as requiring confirmation
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          status: 'cancelled',
          cancellation_reason: reason,
          cancelled_by: userId,
          requires_confirmation: true
        })
        .eq('id', orderId)
        .eq('user_id', userId)
        .select('id, status');
      
      if (error) throw error;
      
      return data?.[0] || { id: orderId, status: 'cancelled' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPurchases', userId] });
      toast({
        title: "Kërkesa për anulim dërguar",
        description: "Kërkesa juaj për anulim është dërguar dhe pret konfirmimin nga shitësi.",
      });
      setCancelDialogOpen(false);
      setCancelOrderId(null);
      setCancellationReason('');
    },
    onError: (error: any) => {
      toast({
        title: "Gabim",
        description: error.message || "Ndodhi një gabim gjatë anulimit të porosisë",
        variant: "destructive",
      });
    },
  });
  
  // Confirm delivery mutation
  const confirmDeliveryMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const { data, error } = await supabase
        .from('orders')
        .update({ status: 'delivered' })
        .eq('id', orderId)
        .eq('user_id', userId)
        .select('id, status');
      
      if (error) throw error;
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPurchases', userId] });
      toast({
        title: "Dorëzimi konfirmuar",
        description: "Konfirmimi i dorëzimit u bë me sukses.",
      });
      setConfirmingDelivery(null);
    },
    onError: (error: any) => {
      toast({
        title: "Gabim",
        description: error.message || "Ndodhi një gabim gjatë konfirmimit të dorëzimit",
        variant: "destructive",
      });
      setConfirmingDelivery(null);
    },
  });
  
  const confirmReceiptMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const { data, error } = await supabase
        .from('orders')
        .update({ status: 'completed' })
        .eq('id', orderId)
        .eq('user_id', userId)
        .select('id, status');
      
      if (error) throw error;
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPurchases', userId] });
      toast({
        title: "Marrja konfirmuar",
        description: "Konfirmimi i marrjes u bë me sukses dhe porosia u shënua si e përfunduar.",
      });
      setConfirmingReceipt(null);
    },
    onError: (error: any) => {
      toast({
        title: "Gabim",
        description: error.message || "Ndodhi një gabim gjatë konfirmimit të marrjes",
        variant: "destructive",
      });
      setConfirmingReceipt(null);
    },
  });
  
  // Helper functions and handlers
  const translateStatus = (status: string): string => {
    const translations: { [key: string]: string } = {
      'pending': 'Në pritje',
      'processing': 'Duke u procesuar',
      'shipped': 'Dërguar',
      'delivered': 'Dorëzuar',
      'completed': 'Përfunduar',
      'cancelled': 'Anuluar'
    };
    
    return translations[status] || status;
  };
  
  // Handler functions
  const handleCancelOrder = (orderId: string) => {
    setCancelOrderId(orderId);
    setCancelDialogOpen(true);
  };
  
  const confirmCancelOrder = () => {
    if (cancelOrderId && cancellationReason.trim()) {
      cancelOrderMutation.mutate({ 
        orderId: cancelOrderId,
        reason: cancellationReason
      });
    } else {
      toast({
        title: "Gabim",
        description: "Ju lutemi shkruani arsyen e anulimit",
        variant: "destructive",
      });
    }
  };
  
  const handleConfirmDelivery = (orderId: string) => {
    setConfirmingDelivery(orderId);
    confirmDeliveryMutation.mutate(orderId);
  };
  
  const handleConfirmReceipt = (orderId: string) => {
    setConfirmingReceipt(orderId);
    confirmReceiptMutation.mutate(orderId);
  };
  
  return {
    purchases,
    isLoading,
    error,
    cancelDialogOpen,
    setCancelDialogOpen,
    confirmingDelivery,
    confirmingReceipt,
    cancellationReason,
    setCancellationReason,
    handleCancelOrder,
    handleConfirmDelivery,
    handleConfirmReceipt,
    confirmCancelOrder
  };
};
